import { useRef, useState } from "react";

type Confidence = "low" | "medium" | "high";
type FieldStatus = "missing" | "review" | "ready";

export type AudioFillField = {
  key: string;
  label: string;
};

type ExtractedField = {
  value: string;
  confidence: Confidence;
  status: FieldStatus;
  evidence: string;
  warnings: string[];
};

type AudioFeedback = {
  type: "success" | "warning" | "error";
  message: string;
};

type AudioFormFillControlProps = {
  fields: AudioFillField[];
  onApply: (values: Record<string, string>, fields: Record<string, ExtractedField>) => void;
  buttonTitle?: string;
};

// Vazio/undefined em build = mesma origem (Worker serve front e /api/*).
// Em `npm start` cai no `wrangler dev` local (npm run poc:api).
const apiBaseUrl = process.env.REACT_APP_AUDIO_POC_API_URL
  ?? (process.env.NODE_ENV === "development" ? "http://localhost:8787" : "");

export default function AudioFormFillControl({ fields, onApply, buttonTitle = "Preencher por áudio" }: AudioFormFillControlProps) {
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<AudioFeedback | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);

  async function transcribeAndApply(audioFile: File, durationSeconds: number | null) {
    setLoading(true);
    setFeedback(null);

    const formData = new FormData();
    formData.append("audio", audioFile);
    if (durationSeconds !== null) formData.append("durationSeconds", String(durationSeconds));

    try {
      const transcriptionResponse = await fetch(`${apiBaseUrl}/api/transcribe`, {
        method: "POST",
        body: formData
      });
      const transcriptionPayload = await transcriptionResponse.json();

      if (!transcriptionResponse.ok || !transcriptionPayload.success) {
        throw new Error(transcriptionPayload.message || "Não foi possível transcrever o áudio.");
      }

      const extractionResponse = await fetch(`${apiBaseUrl}/api/extract-form-fields`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transcriptionId: transcriptionPayload.transcriptionId,
          transcript: transcriptionPayload.transcript,
          requestedFields: fields.map((field) => field.key)
        })
      });
      const extractionPayload = await extractionResponse.json();

      if (!extractionResponse.ok || !extractionPayload.success) {
        throw new Error(extractionPayload.message || "Não foi possível identificar o valor informado.");
      }

      const extractedFields = extractionPayload.extraction.fields;
      const values = fields.reduce<Record<string, string>>((acc, field) => {
        acc[field.key] = extractedFields[field.key]?.value || "";
        return acc;
      }, {});

      const hasAnyValue = Object.values(values).some(Boolean);

      if (!hasAnyValue) {
        throw new Error("Não consegui identificar esse campo no áudio.");
      }

      onApply(values, extractedFields);
      const reviewMessage = fields
        .map((field) => extractedFields[field.key])
        .find((field) => field?.value && field.status === "review" && field.warnings?.length > 0)
        ?.warnings[0];

      setFeedback({
        type: reviewMessage ? "warning" : "success",
        message: reviewMessage || `Entendi: ${transcriptionPayload.transcript}`
      });
    } catch (requestError: any) {
      setFeedback({
        type: "error",
        message: `${requestError.message || "Erro ao preencher por áudio."} Tente novamente ou entre em contato com o suporte.`
      });
    } finally {
      setLoading(false);
    }
  }

  async function startRecording() {
    setFeedback(null);

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setFeedback({
        type: "error",
        message: "Seu navegador não suporta gravação de áudio. Tente novamente em outro navegador ou entre em contato com o suporte."
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      streamRef.current = stream;
      recorderRef.current = recorder;
      recordingStartedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const file = new File([blob], `audio-form-${Date.now()}.webm`, { type: blob.type });
        const durationSeconds = recordingStartedAtRef.current ? (Date.now() - recordingStartedAtRef.current) / 1000 : null;

        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        setRecording(false);
        recordingStartedAtRef.current = null;

        transcribeAndApply(file, durationSeconds);
      };

      recorder.start();
      setRecording(true);
    } catch {
      setFeedback({
        type: "error",
        message: "Não consegui acessar o microfone. Verifique a permissão e tente novamente ou entre em contato com o suporte."
      });
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
  }

  function handleClick() {
    if (loading) return;
    if (recording) stopRecording();
    else startRecording();
  }

  return (
    <div className="audio-fill-inline">
      <button
        className={ `audio-fill-trigger ${recording ? "audio-fill-trigger-recording" : ""} ${loading ? "audio-fill-trigger-loading" : ""}` }
        type="button"
        title={ recording ? "Parar e transcrever" : buttonTitle }
        onClick={ handleClick }
        disabled={ loading }
      >
        <i className={ recording ? "fa-solid fa-stop" : loading ? "fa-solid fa-spinner" : "fa-solid fa-microphone" } aria-hidden="true"></i>
      </button>

      { recording &&
        <span className="audio-fill-inline-status">Gravando... toque para parar</span>
      }

      { loading &&
        <span className="audio-fill-inline-status">Transcrevendo...</span>
      }

      { feedback && !loading && !recording &&
        <span className={ `audio-fill-inline-${feedback.type}` }>{ feedback.message }</span>
      }
    </div>
  );
}
